import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type Task } from '../services/api';
import {
  Plus,
  MoreHorizontal,
  CheckCircle2,
  Circle,
  Clock,
  AlertCircle,
  Calendar,
  Tag,
  Trash2,
  Loader2,
} from 'lucide-react';
import clsx from 'clsx';
import { format, parseISO, isToday, isTomorrow, isPast } from 'date-fns';
import { toast } from 'sonner';

// Format due date in user-friendly way
function formatDueDate(dateStr: string): { text: string; isOverdue: boolean } {
  try {
    // Parse ISO date string - this automatically converts to local timezone
    const date = parseISO(dateStr);
    const now = new Date();

    if (isToday(date)) {
      return { text: 'Today', isOverdue: false };
    }
    if (isTomorrow(date)) {
      return { text: 'Tomorrow', isOverdue: false };
    }
    if (isPast(date) && !isToday(date)) {
      return { text: format(date, 'MMM d'), isOverdue: true };
    }
    // Show year if not current year
    if (date.getFullYear() !== now.getFullYear()) {
      return { text: format(date, 'MMM d, yyyy'), isOverdue: false };
    }
    return { text: format(date, 'MMM d'), isOverdue: false };
  } catch {
    return { text: dateStr, isOverdue: false };
  }
}

const statusColumns = [
  { id: 'todo', label: 'To Do', icon: Circle },
  { id: 'in_progress', label: 'In Progress', icon: Clock },
  { id: 'done', label: 'Done', icon: CheckCircle2 },
] as const;

const priorityColors = {
  urgent: 'bg-error-muted text-error border-error/30',
  high: 'bg-warning-muted text-warning border-warning/30',
  medium: 'bg-info-muted text-info border-info/30',
  low: 'bg-surface-hover text-text-tertiary border-border',
};

const priorityIcons = {
  urgent: AlertCircle,
  high: AlertCircle,
  medium: Circle,
  low: Circle,
};

interface TaskCardProps {
  task: Task;
  onUpdate: (taskId: string, updates: Partial<Task>) => void;
  onDelete: (taskId: string) => void;
}

function TaskCard({ task, onUpdate, onDelete }: TaskCardProps) {
  const [showMenu, setShowMenu] = useState(false);
  const PriorityIcon = priorityIcons[task.priority];

  return (
    <div className="group rounded-lg border border-border bg-surface p-3 hover:border-border-subtle">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-sm truncate">{task.title}</h4>
          {task.description && (
            <p className="mt-1 text-2xs text-text-tertiary line-clamp-2">{task.description}</p>
          )}
        </div>
        <div className="relative">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="rounded p-1 text-text-tertiary opacity-0 hover:bg-surface-hover hover:text-text-secondary group-hover:opacity-100"
          >
            <MoreHorizontal className="h-4 w-4" />
          </button>
          {showMenu && (
            <>
              <div className="fixed inset-0" onClick={() => setShowMenu(false)} />
              <div className="absolute right-0 top-full z-10 mt-1 w-44 rounded-lg border border-border bg-surface py-1 shadow-lg max-h-80 overflow-auto">
                <div className="px-3 py-1 text-2xs font-medium text-text-tertiary uppercase">Status</div>
                {task.status !== 'todo' && (
                  <button
                    onClick={() => {
                      onUpdate(task.taskId, { status: 'todo' });
                      setShowMenu(false);
                    }}
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-sm text-text-secondary hover:bg-surface-hover"
                  >
                    <Circle className="h-4 w-4" />
                    To Do
                  </button>
                )}
                {task.status !== 'in_progress' && (
                  <button
                    onClick={() => {
                      onUpdate(task.taskId, { status: 'in_progress' });
                      setShowMenu(false);
                    }}
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-sm text-text-secondary hover:bg-surface-hover"
                  >
                    <Clock className="h-4 w-4" />
                    In Progress
                  </button>
                )}
                {task.status !== 'done' && (
                  <button
                    onClick={() => {
                      onUpdate(task.taskId, { status: 'done' });
                      setShowMenu(false);
                    }}
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-sm text-text-secondary hover:bg-surface-hover"
                  >
                    <CheckCircle2 className="h-4 w-4" />
                    Done
                  </button>
                )}
                <div className="my-1 border-t border-border" />
                <div className="px-3 py-1 text-2xs font-medium text-text-tertiary uppercase">Priority</div>
                {task.priority !== 'urgent' && (
                  <button
                    onClick={() => {
                      onUpdate(task.taskId, { priority: 'urgent' });
                      setShowMenu(false);
                    }}
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-sm text-error hover:bg-surface-hover"
                  >
                    <AlertCircle className="h-4 w-4" />
                    Urgent
                  </button>
                )}
                {task.priority !== 'high' && (
                  <button
                    onClick={() => {
                      onUpdate(task.taskId, { priority: 'high' });
                      setShowMenu(false);
                    }}
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-sm text-warning hover:bg-surface-hover"
                  >
                    <AlertCircle className="h-4 w-4" />
                    High
                  </button>
                )}
                {task.priority !== 'medium' && (
                  <button
                    onClick={() => {
                      onUpdate(task.taskId, { priority: 'medium' });
                      setShowMenu(false);
                    }}
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-sm text-info hover:bg-surface-hover"
                  >
                    <Circle className="h-4 w-4" />
                    Medium
                  </button>
                )}
                {task.priority !== 'low' && (
                  <button
                    onClick={() => {
                      onUpdate(task.taskId, { priority: 'low' });
                      setShowMenu(false);
                    }}
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-sm text-text-tertiary hover:bg-surface-hover"
                  >
                    <Circle className="h-4 w-4" />
                    Low
                  </button>
                )}
                <div className="my-1 border-t border-border" />
                <button
                  onClick={() => {
                    onDelete(task.taskId);
                    setShowMenu(false);
                  }}
                  className="flex w-full items-center gap-2 px-3 py-1.5 text-sm text-error hover:bg-surface-hover"
                >
                  <Trash2 className="h-4 w-4" />
                  Delete
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <span
          className={clsx(
            'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-2xs',
            priorityColors[task.priority]
          )}
        >
          <PriorityIcon className="h-3 w-3" />
          {task.priority}
        </span>

        {task.dueDate && (() => {
          const { text, isOverdue } = formatDueDate(task.dueDate);
          return (
            <span className={clsx(
              'inline-flex items-center gap-1 text-2xs',
              isOverdue ? 'text-error' : 'text-text-tertiary'
            )}>
              <Calendar className="h-3 w-3" />
              {text}
            </span>
          );
        })()}

        {task.tags?.slice(0, 2).map((tag) => (
          <span
            key={tag}
            className="inline-flex items-center gap-1 rounded-full bg-surface-hover px-2 py-0.5 text-2xs text-text-tertiary"
          >
            <Tag className="h-3 w-3" />
            {tag}
          </span>
        ))}
      </div>
    </div>
  );
}

interface CreateTaskModalProps {
  onClose: () => void;
  onCreate: (task: { title: string; description?: string; priority: Task['priority']; dueDate?: string; tags?: string[] }) => void;
}

function CreateTaskModal({ onClose, onCreate }: CreateTaskModalProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<Task['priority']>('medium');
  const [dueDate, setDueDate] = useState('');
  const [tagsInput, setTagsInput] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;

    const tags = tagsInput
      .split(',')
      .map(t => t.trim())
      .filter(t => t.length > 0);

    onCreate({
      title: title.trim(),
      description: description.trim() || undefined,
      priority,
      dueDate: dueDate || undefined,
      tags: tags.length > 0 ? tags : undefined,
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md rounded-xl border border-border bg-surface p-6 shadow-2xl">
        <h3 className="text-lg font-semibold">Create Task</h3>
        <form onSubmit={handleSubmit} className="mt-4 space-y-4">
          <div>
            <label className="text-sm text-text-secondary">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="input mt-1"
              placeholder="Task title"
              autoFocus
            />
          </div>
          <div>
            <label className="text-sm text-text-secondary">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="input mt-1"
              rows={2}
              placeholder="Optional description"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-text-secondary">Priority</label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value as Task['priority'])}
                className="input mt-1"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <div>
              <label className="text-sm text-text-secondary">Due Date</label>
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="input mt-1"
              />
            </div>
          </div>
          <div>
            <label className="text-sm text-text-secondary">Tags</label>
            <input
              type="text"
              value={tagsInput}
              onChange={(e) => setTagsInput(e.target.value)}
              className="input mt-1"
              placeholder="work, personal, urgent (comma separated)"
            />
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={!title.trim()}>
              Create
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function TaskBoard() {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['tasks'],
    queryFn: () => api.getTasks(),
  });

  const updateMutation = useMutation({
    mutationFn: ({ taskId, updates }: { taskId: string; updates: Partial<Task> }) =>
      api.updateTask(taskId, updates),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tasks'] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (taskId: string) => api.deleteTask(taskId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tasks'] }),
  });

  const createMutation = useMutation({
    mutationFn: api.createTask,
    onSuccess: () => {
      console.log('Task created successfully');
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
    onError: (error) => {
      console.error('Failed to create task:', error);
      toast.error(`Failed to create task: ${error.message}`);
    },
  });

  const tasks = data?.data || [];

  const handleUpdate = (taskId: string, updates: Partial<Task>) => {
    updateMutation.mutate({ taskId, updates });
  };

  const handleDelete = (taskId: string) => {
    deleteMutation.mutate(taskId);
  };

  const handleCreate = (task: Parameters<typeof api.createTask>[0]) => {
    console.log('Creating task:', task);
    // Add default status when creating a task
    createMutation.mutate({ ...task, status: 'todo' as const });
  };

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-accent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full items-center justify-center text-error">
        Failed to load tasks
      </div>
    );
  }

  return (
    <div className="h-full p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Tasks</h2>
          <p className="text-sm text-text-tertiary">{tasks.length} tasks total</p>
        </div>
        <button onClick={() => setShowCreateModal(true)} className="btn-primary">
          <Plus className="h-4 w-4" />
          New Task
        </button>
      </div>

      <div className="grid h-[calc(100%-80px)] grid-cols-3 gap-6">
        {statusColumns.map(({ id, label, icon: Icon }) => {
          const columnTasks = tasks.filter((t) => t.status === id);
          return (
            <div key={id} className="flex flex-col rounded-xl border border-border bg-background p-4">
              <div className="mb-4 flex items-center gap-2">
                <Icon className="h-4 w-4 text-text-tertiary" />
                <h3 className="font-medium">{label}</h3>
                <span className="rounded-full bg-surface px-2 py-0.5 text-xs text-text-tertiary">
                  {columnTasks.length}
                </span>
              </div>
              <div className="flex-1 space-y-3 overflow-auto">
                {columnTasks.map((task) => (
                  <TaskCard
                    key={task.taskId}
                    task={task}
                    onUpdate={handleUpdate}
                    onDelete={handleDelete}
                  />
                ))}
                {columnTasks.length === 0 && (
                  <div className="flex h-32 items-center justify-center rounded-lg border border-dashed border-border text-sm text-text-tertiary">
                    No tasks
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {showCreateModal && (
        <CreateTaskModal
          onClose={() => setShowCreateModal(false)}
          onCreate={handleCreate}
        />
      )}
    </div>
  );
}

